"use client";

// Единое место, где портал сообщает пользователю об ошибке или об успехе.
//
// ЗАЧЕМ. Аудит 31.07 нашёл семь мест, где сбой проглатывался: правка ячейки
// «оставалась» на экране, хотя сервер её отклонил; отметки в воронке молча не
// сохранялись; текст статьи в Базе знаний терялся. Общего механизма показа
// ошибок в проекте не было — каждый экран заводил свой useState("error"), и там,
// где его забывали завести, ошибка исчезала совсем.
//
// Намеренно НЕ контекст: тост нужно звать из обработчиков и .catch() глубоко
// внутри деревьев, куда прокидывать провайдер пришлось бы через десяток
// компонентов. Здесь — крошечная шина событий: toast.error(...) из любого места,
// <ToastHost/> один раз в оболочке портала.

export type ToastKind = "error" | "success" | "info" | "undo";

export type ToastItem = {
  id: number;
  kind: ToastKind;
  text: string;
  /** Действие-повтор: показывается кнопкой рядом с текстом. */
  retry?: () => void;
  /** Отмена действия (№21 каталога движения): пока плашка на экране, действие
   *  можно вернуть. Заменяет окно «Вы уверены?» перед каждым удалением. */
  undo?: () => void;
  /** Сколько плашка живёт, мс — нужно ещё и полосе обратного отсчёта. */
  ttl: number;
};

type Listener = (items: ToastItem[]) => void;

let items: ToastItem[] = [];
let seq = 0;
const listeners = new Set<Listener>();

function emit() {
  const snapshot = items;
  listeners.forEach((l) => l(snapshot));
}

// Таймеры живут отдельно от плашек: их надо уметь останавливать, пока человек
// держит курсор над стопкой. Иначе «Вернуть» исчезает ровно в тот момент, когда
// до него тянутся мышкой.
const timers = new Map<number, { left: number; startedAt: number; handle: ReturnType<typeof setTimeout> }>();
let paused = false;

function arm(id: number, ms: number) {
  const handle = setTimeout(() => { timers.delete(id); dismiss(id); }, ms);
  timers.set(id, { left: ms, startedAt: Date.now(), handle });
}

function push(kind: ToastKind, text: string, retry?: () => void, undo?: () => void) {
  const id = ++seq;
  // Ошибку держим дольше — её надо успеть прочитать; успех гасим быстро.
  // Отмену — шесть секунд: столько человек соображает, что удалил не то.
  const ttl = kind === "error" ? 9000 : kind === "undo" ? 6000 : 3500;
  // Один и тот же текст не дублируем: при массовой операции (перетащили десять
  // карточек при упавшем бэкенде) иначе выросла бы стена одинаковых плашек.
  // ⚠️ Отмены — исключение: удалили три строки подряд, и каждая должна иметь
  // СВОЮ кнопку возврата, даже если подпись совпала.
  const keep = kind === "undo" ? items : items.filter((t) => t.text !== text);
  items = [...keep, { id, kind, text, retry, undo, ttl }];
  emit();
  arm(id, ttl);
  return id;
}

/** Курсор над стопкой — останавливаем обратный отсчёт всех плашек. */
export function pauseToasts() {
  if (paused) return;
  paused = true;
  timers.forEach((t, id) => {
    clearTimeout(t.handle);
    timers.set(id, { ...t, left: Math.max(0, t.left - (Date.now() - t.startedAt)) });
  });
}

export function resumeToasts() {
  if (!paused) return;
  paused = false;
  timers.forEach((t, id) => arm(id, t.left));
}

export function dismiss(id: number) {
  const t = timers.get(id);
  if (t) { clearTimeout(t.handle); timers.delete(id); }
  const next = items.filter((x) => x.id !== id);
  if (next.length !== items.length) {
    items = next;
    emit();
  }
}

export function subscribeToasts(l: Listener) {
  listeners.add(l);
  l(items);
  return () => {
    listeners.delete(l);
  };
}

/** Достаёт человекочитаемую причину из чего угодно, что прилетело в catch. */
export function errText(e: unknown, fallback = "Неизвестная ошибка"): string {
  if (e instanceof Error && e.message) return e.message;
  if (typeof e === "string" && e) return e;
  return fallback;
}

export const toast = {
  error: (text: string, retry?: () => void) => push("error", text, retry),
  success: (text: string) => push("success", text),
  info: (text: string) => push("info", text),
  /** Частый случай: «не удалось сохранить X: <причина>» + повтор. */
  saveFailed: (what: string, e: unknown, retry?: () => void) =>
    push("error", `Не удалось сохранить ${what}: ${errText(e)}`, retry),
  /** Удаление с возвратом вместо окна «Вы уверены?» (№21): действие происходит
   *  сразу, а шесть секунд на экране висит «Вернуть» с полосой отсчёта.
   *  Одним подтверждением меньше на каждое действие, а откат надёжнее: строка
   *  не потеряна, пока полоса не добежала. */
  undo: (text: string, onUndo: () => void) => push("undo", text, undefined, onUndo),
};
